<template>
  <section
    v-if="proposals.length > 0 || shouldShowResponsible"
    class="is-card"
  >
    <h2 class="is-bolder font-md">
      Propostas FGTS
    </h2>
    <p>
      As propostas abaixo já estão em uma etapa avançada. Revise a pendência para conclui-lá com
      sucesso.
    </p>
    <span class="font-sm">
      <strong class="is-bold">
        {{ proposals.length }} proposta {{ proposals.length > 1 ? 's' : '' }}
      </strong>
      averbadas com pendência
    </span>

    <template
      v-if="shouldShowResponsible"
    >
      <base-select
        v-model="form.responsible_id"
        class="responsible-select"
        :options="list.responsible"
        :searching="searching['responsible']"
        can-filter
        has-api-search
        @search="handleResponsibleSearch(
          $event,
          { ...(promoter_id ? { promoter_id } : '') },
          'responsible',
        )"
        @input="getSimplifiedProposalTable"
      >
        Responsável
      </base-select>
    </template>

    <BaseTable
      :headers="[
        { name: 'client', label: 'Cliente' },
        { name: 'status', label: 'Status' },
      ]"
      :data="proposals"
      :actions="[
        {
          label: 'Abrir proposta',
          icon: () => import('@/assets/icons/Link.vue'),
          action: (row) => openProposalList(row.proposal_id),
        },
      ]"
    >
      <template #client="{ row }">
        <div :data-tooltip="`Responsável: ${row.responsible_name}`">
          <p class="is-semi-bold is-highlighted">
            {{ row.document }}
          </p>
          <p>{{ row.name }}</p>
        </div>
      </template>
      <template #status="{ row }">
        <span
          :data-tooltip="`${row.status}${row.obs ? ' - ' + row.obs : ''}`"
          class="tag"
          :class="proposalStatusCypher[row.status]?.tag"
        >
          {{ row.status }}
        </span>
      </template>
      <template #no-data>
        <div class="empty-state">
          <p class="is-bold">
            Não encontramos nenhum resultado para essa solicitação.
          </p>
        </div>
      </template>
    </BaseTable>
    <ThePagination
      :data="pagination.data"
      :storage-key="pagination.filterKey"
      @paginate="getSimplifiedProposalTable"
    />
  </section>
</template>
<script>

import { handler as handlerError } from '@/utils/message';

import {
  accountsAPI,
  proposalsAPI,
} from '@/services';

import config from '@/data/config';

import roleManagerMixin from '@/mixins/roleManager.mixin';
import promoterPaginationMixin from '@/mixins/promoterPagination.mixin';
import loadingMixin from '@/mixins/loadingView.mixin';

import { cypher as proposalStatusCypher } from '@/data/proposalStatusCypher.js';

import ThePagination from '@/components/ThePagination.vue';
import BaseSelect from '@/components/Base/Select/index.vue';
import BaseTable from '@/components/Base/Table/index.vue';

const NOT_FOUND = 404;

export default {
  name: 'SimplifiedProposalTable',
  components: {
    ThePagination,
    BaseSelect,
    BaseTable,
  },
  mixins: [
    promoterPaginationMixin,
    loadingMixin,
    roleManagerMixin,
  ],
  data() {
    return {
      pagination: {
        data: {},
        filterKey: 'hub',
      },
      form: {
        responsible_id: '',
      },
      list: {
        responsible: [],
      },
      proposals: [],
      proposalStatusCypher,
    };
  },
  computed: {
    responsible_id() {
      return this.form.responsible_id;
    },
    shouldShowResponsible() {
      return [
        this.isFinancial,
        this.isSuper,
        this.isManager,
        this.isCommercial,
        this.isCoordenador,
        this.isAdmin,
      ].some((isValid) => isValid);
    },
  },
  mounted() {
    this.getSimplifiedProposalTable();

    // GET /v1/responsibles does not allow the typist role, and the select is
    // only rendered for shouldShowResponsible roles — fetching it unconditionally
    // raised a 403 toast over data this screen would never show
    if (this.shouldShowResponsible) this.getAllResponsibles();
  },
  methods: {
    // handlers
    // the backend answers 404 (custom.not_found) for an empty listing
    // (ProposalRepository@getProposalsListing) — here that is an empty state, not an error
    handleProposalsError(err) {
      if (err?.response?.status === NOT_FOUND) return;

      handlerError(err);
    },

    openProposalList(proposal_id) {
      sessionStorage.setItem('filters', JSON.stringify(
        {
          proposal_id,
        },
      ));

      const { href } = this.$router.resolve({ name: 'fgts-proposal' });
      window.open(href, '_blank');
    },

    // API
    async getSimplifiedProposalTable() {
      this.pagination.data = {};
      this.proposals = [];
      this.loading = true;

      await proposalsAPI
        .getProposals({
          responsible_id: this.responsible_id ? this.responsible_id : null,
          product_id: config.PRODUCT_ID_FGTS,
          status: 'Pendência',
          last_day: true,
        })
        .then(({ data }) => {
          this.pagination.data = data;
          this.proposals = data.data;
        })
        .catch(this.handleProposalsError)
        .finally(() => {
          this.loading = false;
        });
    },
    getAllResponsibles() {
      this.loading = true;

      accountsAPI
        .searchResponsible()
        .then(({ data }) => {
          this.list.responsible = data.data.data.map((elem) => ({
            value: elem.account_id,
            name: elem.name,
          }));
        })
        .catch(handlerError)
        .finally(() => {
          this.loading = false;
        });
    },
  },
};
</script>
<style lang="scss" scoped>
@import '@/assets/scss/src/_textVariables.scss';

.empty-state {
  p {
    text-align: center;
  }
}
.responsible-select {
  margin: 32px 0;
}
p {
  &.is-highlighted {
    color: map.get($stone-types, "900");
  }
}
</style>
